// Default values for the voyage planner. Imported by MainComponent and
// anywhere else that needs sensible starting values before the user has
// loaded a vessel or profile from the DB.

// Navigability category label → numeric code (astar expects the integer).
export const NAV_CAT_MAP = {
  "ECDIS safe": 1,
  "Practically safe": 2,
  "Potentially safe": 3,
  "Practically unsafe": 4,
  Unsafe: 5,
}

// UI speeds are knots, astar/DB basespeed is m/s.
export const KNOTS_TO_MSEC = 0.514444
export const KM_PER_NM = 1.852

export const defaultSpeedProfileData = [
  {
    name: "wind",
    speedprofile_rows_angles: [0, 45, 90, 135, 180, 225, 270, 315, 360],
    speedprofile_columns_strengths: [0, 2, 4, 6, 8, 10],
    speedprofile_percent_speeds: [
      [100, 100, 100, 95, 85, 75],
      [100, 100, 100, 100, 90, 85],
      [100, 100, 100, 100, 100, 100],
      [100, 100, 100, 100, 102, 105],
      [100, 100, 100, 102, 105, 110],
      [100, 100, 100, 100, 102, 105],
      [100, 100, 100, 100, 100, 100],
      [100, 100, 100, 100, 90, 85],
      [100, 100, 100, 95, 85, 75],
    ],
    editable: "true",
  },
  {
    name: "wave",
    speedprofile_rows_angles: [0, 45, 90, 135, 180, 225, 270, 315, 360],
    speedprofile_columns_strengths: [0, 2, 4, 6, 8, 10],
    speedprofile_percent_speeds: [
      [100, 95, 80, 50, 10, 10],
      [100, 97, 90, 60, 30, 15],
      [100, 100, 95, 70, 40, 25],
      [100, 100, 95, 75, 60, 40],
      [100, 100, 98, 85, 75, 50],
      [100, 100, 95, 75, 60, 40],
      [100, 100, 95, 70, 40, 25],
      [100, 97, 90, 60, 30, 15],
      [100, 95, 80, 50, 10, 10],
    ],
    editable: "true",
  },
]

const defaultConsumptionRows = [
  { knots: "12.0", consumption: "20.1", type: "ECO" },
  { knots: "20.0", consumption: "90.0", type: "MAX" },
  { knots: "19.0", consumption: "75.0", type: "DEFAULT" },
  { knots: "18.0", consumption: "60.0", type: "DEFAULT" },
  { knots: "17.0", consumption: "50.0", type: "DEFAULT" },
  { knots: "16.0", consumption: "43.1", type: "DEFAULT" },
  { knots: "15.0", consumption: "37.5", type: "DEFAULT" },
  { knots: "14.0", consumption: "30.9", type: "DEFAULT" },
  { knots: "13.0", consumption: "25.1", type: "DEFAULT" },
  { knots: "11.0", consumption: "15.8", type: "DEFAULT" },
  { knots: "10.0", consumption: "12.2", type: "DEFAULT" },
  { knots: "9.0", consumption: "9.2", type: "DEFAULT" },
  { knots: "8.0", consumption: "6.7", type: "DEFAULT" },
  { knots: "7.0", consumption: "4.7", type: "DEFAULT" },
  { knots: "6.0", consumption: "3.1", type: "DEFAULT" },
  { knots: "5.0", consumption: "2.0", type: "DEFAULT" },
  { knots: "0.0", consumption: "0.0", type: "DEFAULT" },
]

// Fallback in the request-ready shape that /consumption-profiles returns
// (`profile`) — the FE never reshapes consumption rows itself.
export const defaultConsumptionProfile = {
  default: "false",
  draftDependency: 0,
  referenceDraft: 0,
  editable: "true",
  values: defaultConsumptionRows.map((row) => ({
    deletable: row.type === "ECO" || row.type === "MAX" ? "false" : "true",
    consumption: Number(row.consumption),
    knots: Number(row.knots),
    type: row.type,
    editable: "true",
  })),
  name: "Consumption",
  deletable: "true",
}

// Bunker prices (USD/t, standard + ECA): planner start values, and the
// fallback import/recalc send core for routes without prices in their
// snapshot — core keeps no default of its own.
export const DEFAULT_BUNKER_PRICES = { usdPerTon: "700", ecaUsdPerTon: "1300" }

export const defaultBunkerPricesPayload = () => ({
  usdPerTon: Number(DEFAULT_BUNKER_PRICES.usdPerTon),
  ecaUsdPerTon: Number(DEFAULT_BUNKER_PRICES.ecaUsdPerTon),
})

// System default when neither the voyage nor the vessel sets a limit.
export const DEFAULT_WEATHER_LIMITS = { windLimit: 45, waveLimit: 6, currentLimit: 5 }

export const defaultPlannerVesselSettings = {
  // Vessel master data intentionally starts empty — there is no "default
  // vessel". Real values come from the selected vessel's meta and are merged
  // into the request at compute time; computing is blocked until then.
  imo: "",
  lengthM: "",
  beamM: "",
  maxDraftM: "",
  grossTonnage: "",
  shiptype: "",
  nationality: "",
  mmsi: "",
  fleetname: "",
  owner: "",
  charterer: "",
  simplifyEpsilonM: 10000,
  safetyDepthM: 1,
  airDraftM: 1,
  loadstate: "Ballast",
  cargo: {
    DangerousGoods: false,
    RadioactiveMaterial: false,
    MarpolAnnexIICatX: false,
    MarpolAnnexIICatY: false,
    MarpolAnnexIICatZ: false,
    MarpolAnnexIIOthers: false,
    MarpolAnnexIOils: false,
  },
  navigabilityCategory: "Practically safe",
  // Empty = inherit the vessel default (resolveWeatherLimits).
  windLimit: "",
  waveLimit: "",
  currentLimit: "",
}
